import { setActiveCompanyID } from "@/cowork/local-auth"

export type PasswordAuthResponse = {
  access_token: string
  user: { id: string; email: string; name?: string }
  company: { id: string; role: string }
}

export type InvitationPreview = {
  email: string
  role: "viewer" | "member" | "admin" | "owner"
  workspace_name: string
  invited_by?: string | null
  account_exists: boolean
  expires_at: string
}

export class AuthApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail)
  }
}

export function authApiBaseUrl() {
  return (import.meta.env.VITE_AGENT_API_URL || "http://localhost:8088").replace(/\/$/, "")
}

export function signIn(email: string, password: string) {
  return postJson<PasswordAuthResponse>("/api/auth/signin", { email, password })
}

export function verifyEmail(token: string) {
  return postJson<PasswordAuthResponse>("/api/auth/verify", { token })
}

export function resendVerification(email: string) {
  return postJson<Record<string, never>>("/api/auth/resend-verification", { email })
}

export function requestPasswordReset(email: string) {
  return postJson<Record<string, never>>("/api/auth/password-reset", { email })
}

export function confirmPasswordReset(token: string, password: string) {
  return postJson<PasswordAuthResponse>("/api/auth/password-reset/confirm", { token, password })
}

export async function previewInvitation(token: string) {
  const response = await fetch(`${authApiBaseUrl()}/api/auth/invitations/${encodeURIComponent(token)}`).catch(() => {
    throw new AuthApiError(0, "auth.network_error")
  })
  if (!response.ok) throw new AuthApiError(response.status, await errorDetail(response))
  return (await response.json()) as InvitationPreview
}

export function acceptInvitationWithNewAccount(token: string, fullName: string, password: string) {
  return postJson<PasswordAuthResponse>(`/api/auth/invitations/${encodeURIComponent(token)}/accept`, {
    full_name: fullName,
    password,
  })
}

export async function acceptInvitationWithSession(token: string, accessToken: string) {
  const response = await fetch(`${authApiBaseUrl()}/api/workspace/invitations/${encodeURIComponent(token)}/accept`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  }).catch(() => {
    throw new AuthApiError(0, "auth.network_error")
  })
  if (!response.ok) throw new AuthApiError(response.status, await errorDetail(response))
  return (await response.json()) as { company_id: string; role: string }
}

export function storeAuth(body: PasswordAuthResponse) {
  try {
    localStorage.removeItem("activeCompanyId")
    localStorage.removeItem("cowork.agent.companyId")
    // Workspace resources must not start with a previous account's company scope.
    localStorage.removeItem("cowork.workspace.entityId")
    localStorage.removeItem("cowork.workspace.projectId")
    localStorage.removeItem("cowork.workspace.periodId")
    localStorage.removeItem("cowork.agent.selectedProjectId")
    localStorage.removeItem("cowork.agent.selectedWorkflowId")
    localStorage.removeItem("cowork.agent.selectedAgent")
    localStorage.removeItem("cowork.agent.centralView")
    localStorage.setItem("cowork.auth.token", body.access_token)
    localStorage.setItem("cowork.auth.email", body.user.email)
    localStorage.setItem("cowork.auth.name", body.user.name || body.user.email)
    localStorage.setItem("cowork.agent.userId", body.user.id)
    localStorage.setItem("cowork.agent.userEmail", body.user.email)
    setActiveCompanyID(body.company.id)
  } catch {
    return
  }
}

export function clearStoredAuth() {
  try {
    sessionStorage.removeItem("cowork.activeCompanyId")
  } catch {
    // Continue clearing the persisted session if tab storage is unavailable.
  }
  try {
    localStorage.removeItem("activeCompanyId")
    localStorage.removeItem("cowork.agent.companyId")
    localStorage.removeItem("cowork.auth.token")
    localStorage.removeItem("cowork.auth.email")
    localStorage.removeItem("cowork.auth.name")
  } catch {
    return
  }
}

async function postJson<T>(path: string, body: unknown) {
  const response = await fetch(`${authApiBaseUrl()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch(() => {
    throw new AuthApiError(0, "auth.network_error")
  })
  if (!response.ok) throw new AuthApiError(response.status, await errorDetail(response))
  return (await response.json()) as T
}

async function errorDetail(response: Response) {
  if (response.status >= 500) return "auth.service_unavailable"
  try {
    const payload = (await response.json()) as { detail?: unknown }
    if (typeof payload.detail === "string" && payload.detail.trim()) return payload.detail
  } catch {
    return "auth.request_failed"
  }
  return "auth.request_failed"
}
